/*
  Styles for the Sentio Mind mobile-app showcase page.
  Same brand tokens as the platform landing page (teal → indigo, Inter / Poppins /
  DM Sans), re-tuned for a lighter, airier "floating through the sky" surface.
  Navbar, drawer and footer styling is NOT here — it lives in SiteChrome.
*/
export const mobileStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600&family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ma-teal: #2bb3a3;
    --ma-indigo: #6366f1;
    --ma-ink: #0d2437;
    --ma-body: #4d6b80;
    --ma-sky: #eaf6ff;
    --nav-height: 116px;

    /* ── BREAKPOINT SCALE ──────────────────────────────────────────────────
       One ladder for the whole page, close to Apple's own:
         <= 479px   small phone
         480-733    large phone / phablet
         734-1068   tablet (iPad portrait and landscape)
         1069-1441  laptop / small desktop
         >= 1442    large desktop
       These five lines are reserved for *structural* changes only — columns
       stacking, the orbit becoming a row, the chapter mechanic switching off.
       Everything else (padding, type, phone-shot sizing) is carried by clamp()
       so it scales continuously and nothing jumps as you cross a line. */

    /* Page gutter: 16px on a 320 phone, ~29 at the tablet line, 60 on desktop.
       Every band, the hero and the chapter intro read off this one number. */
    --ma-gutter: clamp(16px, 4vw, 60px);
    --ma-band-y: clamp(62px, 9vw, 118px);

    /* Notches and home indicators. Declared once with a 0px fallback so the
       calc() chains below stay valid in browsers without env(). */
    --ma-safe-l: env(safe-area-inset-left, 0px);
    --ma-safe-r: env(safe-area-inset-right, 0px);
    --ma-safe-t: env(safe-area-inset-top, 0px);
    --ma-safe-b: env(safe-area-inset-bottom, 0px);
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'Inter', 'DM Sans', sans-serif;
    background: #f2fbff;
    color: var(--ma-ink);
    overflow-x: hidden;
  }

  /* clip, not hidden. overflow-x: hidden resolves overflow-y to auto, which makes
     this a scroll container — and a scroll container between the viewport and a
     position: sticky element is what that element sticks to. .ma-chapters-stage
     would anchor itself to a box that never scrolls and behave as if it were
     static. clip contains the same horizontal spill without ever becoming a
     scrollport. hidden stays first as the fallback for browsers without clip. */
  #root, #__next {
    display: block !important;
    width: 100% !important;
    overflow-x: hidden !important;
    overflow-x: clip !important;
  }

  ::selection { background: rgba(43,179,163,0.25); }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #e0f2fe; }
  ::-webkit-scrollbar-thumb { background: var(--ma-teal); border-radius: 3px; }

  #demo-page-root {
    position: fixed;
    inset: 0;
    z-index: 10001;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: #f0f9ff;
  }

  /* ═══ PAGE SHELL ═══ */
  .ma-page {
    display: block;
    width: 100%;
    max-width: 100vw;
    /* Same reason as #root above — clip, so the sticky chapter stage keeps the
       viewport as its scrollport instead of anchoring to this element. */
    overflow-x: hidden;
    overflow-x: clip;
    /* A much lighter, cloudless blue for everything below the hero */
    background:
      linear-gradient(180deg,
        #eef8ff 0%,
        #f4fbff 22%,
        #ffffff 48%,
        #f2fafe 74%,
        #e9f5ff 100%);
    position: relative;
  }
  .ma-page img { max-width: 100%; }
  #ma-home { scroll-margin-top: var(--nav-height); }

  /* Soft ambient light blooms — the only "atmosphere" below the hero */
  /* Sized inline in px for the desktop composition; capped here so a 620px
     bloom doesn't become the whole viewport — and blurred less on small
     screens, where a 90px blur radius on a 620px circle is pure GPU cost for
     something you can barely see. */
  .ma-bloom {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    pointer-events: none;
    z-index: 0;
    max-width: 74vw;
    max-height: 74vw;
  }
  @media (max-width: 733px) {
    .ma-bloom { filter: blur(60px); opacity: 0.7; }
  }

  /* ═══ MOTION PRIMITIVES ═══ */
  @keyframes maFloat {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(0, -16px, 0); }
  }
  @keyframes maFloatSoft {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(0, -10px, 0); }
  }
  @keyframes maPulseRing {
    0%   { transform: scale(1);   opacity: 0.6; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes maShimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes maFadeUp {
    from { opacity: 0; transform: translateY(26px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  /* The tagline enters like everything else in the hero, but it also sits at a
     permanent tilt — so the rotation has to travel through the keyframes or
     the animation would flatten it for its first 0.9s. */
  @keyframes maTaglineIn {
    from { opacity: 0; transform: rotate(var(--ma-tagline-tilt)) translateY(26px); }
    to   { opacity: 1; transform: rotate(var(--ma-tagline-tilt)) translateY(0); }
  }
  @keyframes maDrift {
    0%   { transform: translate3d(0, 0, 0); }
    100% { transform: translate3d(60px, -18px, 0); }
  }

  /* ── LAUNCH BADGE MOTION ──
     Four slow loops on four different elements, on purpose: the wrapper bobs,
     the pill breathes its glow, each sparkle twinkles on its own clock, and a
     light sweep crosses the glass. Nothing shares a duration, so the badge
     never falls into a visible common beat. */
  @keyframes maFlagFloat {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(0, -5px, 0); }
  }
  @keyframes maFlagGlow {
    0%, 100% { box-shadow: 0 0 18px rgba(255,255,255,0.18); }
    50%      { box-shadow: 0 0 30px rgba(255,255,255,0.34); }
  }
  @keyframes maFlagTwinkle {
    0%, 100% { transform: scale(0.85); opacity: 0.4; }
    50%      { transform: scale(1.1);  opacity: 1; }
  }
  /* The sweep is over in the first fifth of the cycle; the rest of the loop is
     the pause that keeps it a glint rather than a strobe. */
  @keyframes maFlagShimmer {
    0%        { transform: translateX(-160%) skewX(-18deg); }
    20%, 100% { transform: translateX(160%)  skewX(-18deg); }
  }

  /* Scroll reveal — one class, staggered through --ma-delay */
  .ma-reveal {
    opacity: 0;
    transform: translateY(30px);
    transition:
      opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1) var(--ma-delay, 0s),
      transform 0.9s cubic-bezier(0.22, 1, 0.36, 1) var(--ma-delay, 0s);
    will-change: opacity, transform;
  }
  .ma-reveal.ma-in { opacity: 1; transform: translateY(0); }

  /* ═══ HERO ═══ */
  /* svh, deliberately — not vh and not dvh. vh is the *largest* viewport and
     puts the bottom of the hero under mobile Safari's address bar; dvh is
     correct at any instant but re-measures continuously as that bar collapses,
     so the hero grows mid-scroll and the whole page below it shifts. svh is
     the smallest viewport: one value, never re-measured, nothing ever
     underneath the chrome. The bar retracting simply reveals more sky. */
  .ma-hero {
    position: relative;
    width: 100%;
    min-height: 100svh;
    display: flex;
    align-items: center;
    padding:
      calc(var(--nav-height) + clamp(18px, 3vw, 40px))
      calc(var(--ma-gutter) + var(--ma-safe-r))
      clamp(54px, 7vw, 90px)
      calc(var(--ma-gutter) + var(--ma-safe-l));
    overflow: hidden;
    isolation: isolate;
    background: #5ec4f5;
  }
  .ma-hero-bg {
    position: absolute;
    inset: -8% 0 -12%;
    z-index: 0;
    background-size: cover;
    background-position: center 30%;
    will-change: transform;
  }
  /* Blends the photographic sky into the flat, cloudless page below it */
  .ma-hero-fade {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 34%;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(238,248,255,0) 0%, rgba(238,248,255,0.72) 62%, #eef8ff 100%);
  }
  .ma-hero-tint {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(105deg, rgba(9,63,102,0.34) 0%, rgba(9,63,102,0.10) 46%, rgba(255,255,255,0) 72%);
  }

  .ma-hero-inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1320px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1.02fr 0.98fr;
    gap: 48px;
    align-items: center;
  }

  .ma-eyebrow {
    display: inline-flex; align-items: center; gap: 9px;
    padding: 8px 18px; border-radius: 100px;
    border: 1px solid rgba(255,255,255,0.45);
    background: rgba(255,255,255,0.18);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    font-size: 12px; font-weight: 600; color: #ffffff;
    letter-spacing: 2px; text-transform: uppercase;
    animation: maFadeUp 0.9s ease 0.1s both;
  }
  .ma-eyebrow-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #ffffff; position: relative; flex-shrink: 0;
  }
  .ma-eyebrow-dot::before {
    content: ''; position: absolute; inset: -3px; border-radius: 50%;
    border: 1.5px solid rgba(255,255,255,0.6);
    animation: maPulseRing 1.8s ease-out infinite;
  }

  /* Two ranges, because the headline tracks its *column*, not the viewport:
     above the tablet line it lives in a ~48% column, below it the hero is
     stacked and it owns the full width. One vw curve can't serve both without
     reading huge at 1069 or tiny at 734. Both curves are continuous inside
     their range, and they meet at ~49px across the 1068/1069 line so the
     switch itself is invisible. The 1069+ curve is in the base rule; the
     stacked curve is in the tablet query below. */
  .ma-hero h1 {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(44px, 4.6vw, 78px);
    font-weight: 700;
    line-height: 1.06;
    /* Tracking has to ease with the size or it eats the counters at 34px */
    letter-spacing: clamp(-1.5px, -0.019em, -0.6px);
    color: #ffffff;
    margin-top: 26px;
    text-shadow: 0 8px 40px rgba(6,48,80,0.28);
    animation: maFadeUp 0.9s ease 0.22s both;
    /* Kills the one-word last line at every width in the QA matrix */
    text-wrap: balance;
  }
  .ma-hero h1 .ma-h1-soft {
    display: block;
    color: rgba(255,255,255,0.86);
    font-weight: 400;
  }
  .ma-hero-lede {
    font-size: clamp(16px, 0.55vw + 14px, 21px);
    line-height: 1.72;
    color: rgba(255,255,255,0.92);
    max-width: 560px;
    margin-top: 24px;
    text-shadow: 0 2px 18px rgba(6,48,80,0.3);
    animation: maFadeUp 0.9s ease 0.34s both;
    text-wrap: pretty;
  }

  /* The launch date — the first thing read in the hero, so the page states
     up front that the app is not out yet. Three nested elements because three
     transforms need to run at once without fighting: the wrapper takes the
     entry fade, the row takes the float, the sparkles take the twinkle. */
  .ma-hero-flagwrap {
    margin-bottom: 22px;
    animation: maFadeUp 0.9s ease 0.1s both;
  }
  .ma-hero-flagrow {
    display: inline-flex; align-items: center;
    /* Half the gap plus half a sparkle puts each mark's centre ~35px clear of
       the pill. Laying them out in flow rather than absolutely means a narrow
       viewport shrinks the badge instead of pushing the marks off-screen. */
    gap: clamp(10px, 1.6vw, 18px);
    animation: maFlagFloat 4s ease-in-out infinite;
  }
  /* Two-column hero is left-aligned: hang the left sparkle out into the
     gutter so the pill itself, not the mark, lines up with the heading. The
     stacked layout centres the row, so the offset would only skew it there. */
  @media (min-width: 1069px) {
    .ma-hero-flagrow { margin-left: calc(-34px - clamp(10px, 1.6vw, 18px)); }
  }

  .ma-flag-spark {
    flex-shrink: 0;
    transform-origin: 50% 50%;
    animation: maFlagTwinkle 2.2s ease-in-out infinite;
  }
  /* Different clocks and an offset start, so the two marks never twinkle
     together — a matched pair reads as a mechanism, not a shimmer. */
  .ma-flag-spark-r { animation-duration: 2.8s; animation-delay: 0.6s; }

  .ma-hero-flag {
    position: relative;
    overflow: hidden;
    display: inline-flex; align-items: center;
    margin: 0;
    padding: 13px 22px;
    border-radius: 999px;
    background: rgba(255,255,255,0.22);
    border: 1px solid rgba(255,255,255,0.45);
    backdrop-filter: blur(14px) saturate(150%);
    -webkit-backdrop-filter: blur(14px) saturate(150%);
    font-family: 'Poppins', sans-serif;
    font-size: clamp(14px, 0.55vw + 12.2px, 17px);
    font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase;
    color: #ffffff;
    box-shadow: 0 0 24px rgba(255,255,255,0.25);
    animation: maFlagGlow 3s ease-in-out infinite;
  }
  /* Glass catches a light source that is not there — a diagonal band riding
     across the pill on a long duty cycle. */
  .ma-hero-flag::after {
    content: '';
    position: absolute; top: 0; bottom: 0; left: 0;
    width: 45%;
    pointer-events: none;
    background: linear-gradient(
      100deg,
      rgba(255,255,255,0) 0%,
      rgba(255,255,255,0.38) 50%,
      rgba(255,255,255,0) 100%
    );
    animation: maFlagShimmer 4.5s ease-in-out 1.2s infinite;
  }

  .ma-hero-cta {
    display: flex; flex-wrap: wrap; gap: 14px; margin-top: 38px;
    animation: maFadeUp 0.9s ease 0.46s both;
  }

  .ma-hero-stores {
    display: flex; flex-wrap: wrap; gap: 12px; margin-top: 22px;
    animation: maFadeUp 0.9s ease 0.58s both;
  }
  /* Three claims and two dots on one line. Without flex-wrap the row can't
     break, so under ~480px each claim was shrunk into its own narrow column
     and wrapped internally — three ragged stacks instead of one line. Wrapping
     lets the row break between claims, which is the only place it should. */
  .ma-hero-note {
    display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px;
    margin-top: 26px;
    font-size: clamp(13px, 0.2vw + 12.4px, 14px);
    font-weight: 600; letter-spacing: 0.4px;
    color: #0B7566;
    animation: maFadeUp 0.9s ease 0.7s both;
  }
  .ma-hero-note span {
    width: 4px; height: 4px; border-radius: 50%;
    background: rgba(11,117,102,0.6);
    flex-shrink: 0;
  }

  /* ═══ BUTTONS ═══ */
  .ma-btn-primary {
    padding: 17px 34px; border-radius: 100px; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 17px; font-weight: 600;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    color: white; letter-spacing: 0.3px;
    transition: transform 0.3s, box-shadow 0.3s;
    position: relative; overflow: hidden;
    min-height: 54px;
    box-shadow: 0 10px 30px rgba(20,90,120,0.24);
  }
  .ma-btn-primary::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transform: translateX(-100%); transition: transform 0.5s;
  }
  .ma-btn-primary:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(43,179,163,0.42); }
  .ma-btn-primary:hover::after { transform: translateX(100%); }

  .ma-btn-glass {
    padding: 17px 34px; border-radius: 100px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 17px; font-weight: 600;
    color: #ffffff; letter-spacing: 0.3px;
    background: rgba(255,255,255,0.16);
    border: 1.5px solid rgba(255,255,255,0.5);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    transition: all 0.3s;
    min-height: 54px;
  }
  .ma-btn-glass:hover { background: rgba(255,255,255,0.3); border-color: #ffffff; transform: translateY(-3px); }

  .ma-btn-ghost {
    padding: 17px 34px; border-radius: 100px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 17px; font-weight: 600;
    background: rgba(255,255,255,0.7); color: #2f5d70;
    border: 1.5px solid rgba(43,179,163,0.35);
    transition: all 0.3s;
    min-height: 54px;
  }
  .ma-btn-ghost:hover { border-color: #2bb3a3; color: #2bb3a3; background: #ffffff; transform: translateY(-3px); }

  /* ═══ STORE BADGES ═══ */
  .ma-store {
    display: inline-flex; align-items: center; gap: 12px;
    padding: 11px 22px 11px 18px;
    border-radius: 16px;
    text-decoration: none;
    cursor: pointer;
    background: rgba(9, 30, 45, 0.72);
    border: 1px solid rgba(255,255,255,0.22);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    color: #ffffff;
    transition: transform 0.3s, background 0.3s, box-shadow 0.3s;
    min-height: 56px;
  }
  .ma-store:hover {
    transform: translateY(-3px);
    background: rgba(9, 30, 45, 0.88);
    box-shadow: 0 14px 34px rgba(9,30,45,0.3);
  }
  .ma-store svg { flex-shrink: 0; }
  .ma-store-text { display: flex; flex-direction: column; line-height: 1.15; text-align: left; }
  .ma-store-small { font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; opacity: 0.72; }
  .ma-store-big { font-family: 'Poppins', sans-serif; font-size: 17px; font-weight: 600; letter-spacing: -0.2px; }
  .ma-store.ma-store-light {
    background: rgba(13, 36, 55, 0.95);
    border-color: rgba(13,36,55,0.2);
  }
  /* Pre-launch: the badge is a marker, not a link. It keeps the shape but drops
     the affordances, so nothing invites a click that would go nowhere. */
  .ma-store-soon {
    cursor: default;
    opacity: 0.88;
  }
  .ma-store-soon:hover {
    transform: none;
    background: rgba(9, 30, 45, 0.72);
    box-shadow: none;
  }
  .ma-store-light.ma-store-soon:hover { background: rgba(13, 36, 55, 0.95); }

  /* ═══ TOUCH TARGETS ═══
     Everything interactive clears 44x44 on a coarse pointer. The buttons and
     store badges already do by their own padding; the nav pill's links are
     text nodes ~16px tall, so they get an explicit box that grows the hit area
     without moving the glyphs — the pill's own height absorbs it. */
  @media (pointer: coarse) {
    .ma-btn-primary, .ma-btn-glass, .ma-btn-ghost { min-height: 48px; }
    .ma-store { min-height: 56px; }
    .ma-page a, .ma-page button { touch-action: manipulation; }
  }

  .ma-float { animation-play-state: paused; }
  .ma-in .ma-float, .ma-float.ma-in {
    animation: maFloat 7.5s ease-in-out infinite;
    animation-delay: var(--ma-float-delay, 0s);
    animation-play-state: running;
  }

  /* ═══ FINISHED PHONE RENDERS ═══
     Every screen asset is a complete device render — body,
     bezel, angle and contact shadow are baked into the PNG, on a larger
     transparent canvas. Nothing here frames, masks, tilts or rotates them:
       --ma-body-h    how tall the phone *body* should read in the layout
       --ma-canvas-k  canvas height / body height, so the canvas scales to suit
       --ma-shot-dx   nudge so the body, not the canvas, is what gets centred
     The float is maFloat, delay and all.

     Two more come from the same measurement pass and exist only so the podium
     can be sized against the *device*, never against the canvas around it:
       --ma-canvas-ar canvas width / canvas height
       --ma-shot-w    body width / canvas width — on the tilted renders this is
                      the rotated bounding box, i.e. the real visual footprint
     Together they give the phone's rendered body width:
       --ma-body-h * --ma-canvas-k * --ma-canvas-ar * --ma-shot-w             */
  .ma-shot-stage {
    --ma-body-h: clamp(340px, 40vw, 560px);
    position: relative;
    display: flex;
    justify-content: center;
    opacity: 0;
    transform: translateY(30px);
    transition:
      transform 1.4s cubic-bezier(0.2, 0.9, 0.24, 1),
      opacity 1s ease;
    will-change: opacity, transform;
  }
  .ma-shot-stage.ma-in { opacity: 1; transform: translateY(0); }
  .ma-page .ma-shot {
    display: block;
    height: calc(var(--ma-body-h) * var(--ma-canvas-k, 1));
    width: auto;
    max-width: none;
    transform: translateX(var(--ma-shot-dx, 0));
    pointer-events: none;
  }
  /* The device always reads in front of the environment plate. */
  .ma-shot-stage > .ma-float,
  .ma-shot-stage > .ma-shot { position: relative; z-index: 1; }

  /* ═══ PODIUM / NATURE PLATE ═══
     leftpodium.png and rightpodium.png are complete environments — plinth,
     mountains, foliage, sparkles — on a transparent canvas. They sit *behind*
     the finished render, anchored so the plinth's top face lands on the
     device's lowest point, which is what makes the phone read as grounded.

     The plate is sized from the phone's rendered body width, never from the
     canvas or the column — that is the only way one set of numbers survives
     both an upright render and a tilted one:

       platform width = phone body width * --ma-pod-grip
       plate width    = platform width   * --ma-pod-span

     --ma-pod-grip is how much platform the device gets: 1.45x an upright body
     wide, 1.25x a tilted one (a tilted body already presents a much wider
     footprint, so it needs less platform). The reference comps ran wider —
     1.8 / 1.44 — but at that scale the plate's outer artwork reached across
     the gutter and sat under the copy, so the platform is drawn in to the
     tightest ratio that still reads as visibly wider than the device.
     --ma-pod-span converts that platform back into a whole-plate width, since
     the plinth is only part of each asset.

     Measured once from each asset's own box, as fractions of it:
       --ma-pod-ar    height / width, so one number scales the whole plate
       --ma-pod-disc  vertical centre of the plinth's top ellipse
       --ma-pod-cx    horizontal centre of that ellipse
       --ma-pod-span  plate width / width of the top face
       --ma-pod-foot  where the plate's pixels actually stop, so stacked
                      layouts don't reserve its empty bottom margin
     Every offset is derived from --ma-body-h, so the plate keeps its exact
     relationship to the phone at every breakpoint instead of being pinned. */
  .ma-staged {
    --ma-pod-grip: 1.25;
    --ma-plate-w: calc(
      var(--ma-body-h) * var(--ma-canvas-k) * var(--ma-canvas-ar)
      * var(--ma-shot-w) * var(--ma-pod-grip) * var(--ma-pod-span)
    );
  }
  /* Renders that stand square to the camera carry the wider reference platform */
  .ma-staged-upright { --ma-pod-grip: 1.45; }

  /* The .ma-page img rule up top sets max-width, so this has to outrank it or
     the plate is silently squashed down to the width of the stage box. */
  .ma-page .ma-podium {
    position: absolute;
    z-index: 0;
    left: 50%;
    /* Drop the top face onto the device's lowest point. --ma-shot-sink then
       presses a steeply tilted render a little further into the face, so its
       leading corner rests on the platform instead of grazing the back rim. */
    top: calc(
      var(--ma-body-h) * (
        var(--ma-shot-bottom, 0.95) * var(--ma-canvas-k, 1) - var(--ma-shot-sink, 0)
      )
      - var(--ma-pod-disc) * var(--ma-pod-ar) * var(--ma-plate-w)
    );
    width: var(--ma-plate-w);
    height: auto;
    max-width: none;
    /* Centre the top face on the device's *footprint* — where it actually
       touches down — which on a tilted render is well off the canvas centre. */
    transform: translateX(calc(
      var(--ma-pod-cx) * -100%
      + var(--ma-body-h) * var(--ma-canvas-k) * var(--ma-canvas-ar) * var(--ma-shot-fx, 0)
    ));
    pointer-events: none;
    user-select: none;
  }
  /* 1536 x 1024 — top face 41.5% of the plate wide, centred at 49.55% / 75.87% */
  .ma-staged-left  { --ma-pod-span: 2.4094; --ma-pod-ar: 0.6667; --ma-pod-disc: 0.7587; --ma-pod-cx: 0.4955; --ma-pod-foot: 0.9365; }
  /* 1536 x 1024 — top face 46.55% of the plate wide, centred at 51.14% / 71.83%.
     These were previously carried over from a 666 x 375 version of the asset
     that no longer ships, which put --ma-pod-ar at 0.5631 against a plate the
     browser actually lays out at 0.6667 (height is auto, so the render follows
     the file, not this number). Everything downstream is derived from it, so
     the plate came out 1.53x too wide, its top face landed ~0.17 body heights
     below the device — the phone hovered over the plinth instead of standing on
     it — and the stacked padding-bottom under-reserved by the same amount, so
     the plate's base ran on into the label and heading below. Re-measured from
     rightpodium.png's own pixels, the same way the left plate's numbers were. */
  .ma-staged-right { --ma-pod-span: 2.1483; --ma-pod-ar: 0.6667; --ma-pod-disc: 0.7183; --ma-pod-cx: 0.5114; --ma-pod-foot: 0.9385; }

  /* Even at the tightened grip the plate is 2.4-3.3x its own plinth, so its
     mountains and foliage still reach across the gutter and under the copy.
     Rather than shrink the plinth below the device — which is what makes the
     phone read as grounded — the artwork is faded out on whichever side the
     text sits, so the copy always has clean ground beneath it.

     The stops are asset-relative, like every other number here: they start at
     the plinth's own outer edge (--ma-pod-cx +/- half of 1/--ma-pod-span) so
     the platform itself is never touched, only the scenery past it.
       left plate  — top face ends at 70.3%, fade 71% -> 80%
       right plate — top face starts at 27.9%, fade 27% -> 18% (leftwards)   */
  .ma-staged-left > .ma-podium {
    -webkit-mask-image: linear-gradient(to right, #000 0 71%, transparent 80%);
            mask-image: linear-gradient(to right, #000 0 71%, transparent 80%);
  }
  .ma-staged-right > .ma-podium {
    -webkit-mask-image: linear-gradient(to right, transparent 0 18%, #000 27%);
            mask-image: linear-gradient(to right, transparent 0 18%, #000 27%);
  }

  /* ═══ HERO PHONE CLUSTER ═══
     Home-1.png and WellBeing.png are finished renders — body, bezel, angle and
     contact shadow are all baked into the PNG. No CSS frame, tilt, border or
     mask goes near them; we only place and scale them.

     Each phone sits off-centre inside a larger transparent canvas, so the
     wrapper anchors by the *body* box rather than the image box:
       --ma-canvas-k  canvas width / body width (scale up to hit a body width)
       left/top       where in the cluster that anchor point lands
       transform      pulls the image so the named body point meets the anchor  */
  .ma-hero-cluster {
    position: relative;
    width: 100%;
    /* The phones are absolutely placed, so the cluster needs its own height.
       Tying it to width keeps the pair proportional at every breakpoint. */
    aspect-ratio: 1 / 1.05;
  }
  .ma-hero-phone {
    position: absolute;
    width: calc(var(--ma-body-w) * var(--ma-canvas-k));
    pointer-events: none;
  }
  .ma-hero-phone img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    animation: maFloat 7.5s ease-in-out infinite;
    animation-delay: var(--ma-float-delay, 0s);
    will-change: transform;
  }
  /* Home-1.png — body is 56.35% of canvas wide, centred at (54.35%, 42.84%).
     Body centre anchored left-of-middle so it reads as the nearer phone. */
  .ma-hero-phone-front {
    z-index: 2;
    --ma-body-w: 50%;
    --ma-canvas-k: 1.7746;
    left: 22%;
    top: 49%;
    transform: translate(-54.35%, -42.84%);
  }
  /* WellBeing.png — body is 33.46% of canvas wide; its right edge sits at
     70.31% and its top at 4.59% of the canvas. Anchored by that right edge
     to the cluster's edge and raised, matching the reference pairing. */
  .ma-hero-phone-back {
    z-index: 1;
    --ma-body-w: 48%;
    --ma-canvas-k: 2.9884;
    left: 100%;
    top: 8%;
    transform: translate(-70.31%, -4.59%);
    opacity: 0.94;
  }
  .ma-hero-halo {
    position: absolute;
    left: 50%; top: 50%;
    transform: translate(-50%, -50%);
    width: 118%; aspect-ratio: 1;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0) 68%);
    z-index: 0;
    pointer-events: none;
  }

  /* ── "Small Steps / Brighter Days" ─────────────────────────────────────────
     A hand-written note in the sky, off the right shoulder of the phone pair.
     It anchors at left: 100% of .ma-hero-inner — i.e. the container edge the
     phones' right edge also sits on — so the pair and the note keep the same
     relationship at every width instead of being pinned to one viewport.
     margin-left and font-size are the two fluid numbers: the sky between that
     container edge and the window is (100vw - 1440px) / 2 + 60 wide, so both
     track vw and the note grows only as fast as the room it has. */
  .ma-hero-tagline {
    position: absolute;
    top: 24%;
    left: 100%;
    margin-left: clamp(-2px, 14.58vw - 234px, 60px);
    width: max-content;
    z-index: 3;
    /* Decoration only — never in the way of a tap or the CTA behind it. */
    pointer-events: none;
    -webkit-user-select: none;
    user-select: none;
    font-family: 'Caveat', 'Segoe Script', cursive;
    font-weight: 500;
    /* ~35px at 1600, 58 from 1920 up — the ceiling the reference is drawn at,
       and still a long way short of the headline it sits beside. */
    font-size: clamp(26px, 7.08vw - 78px, 58px);
    line-height: 0.9;
    color: #ffffff;
    text-shadow: 0 3px 20px rgba(9,63,102,0.34);
    /* One tilt for the whole note, not four. Turning the block rather than the
       lines is what puts each word a little further down-and-right than the one
       above it, the way a hand writing at an angle does. */
    --ma-tagline-tilt: -5.5deg;
    transform: rotate(var(--ma-tagline-tilt));
    transform-origin: right center;
    animation: maTaglineIn 0.9s ease 0.45s both;
  }
  .ma-hero-tagline span {
    display: block;
    position: relative;
    /* Each line's box hugs its own word, so the stroke offsets below are read
       against the word rather than against the widest line in the block. */
    width: max-content;
  }
  .ma-hero-tagline span:nth-child(2) { margin-left: 0.14em; }
  .ma-hero-tagline span:nth-child(3) { margin-left: 0.3em; }
  .ma-hero-tagline span:nth-child(4) { margin-left: 0.5em; }
  .ma-tagline-accent { color: #e3f573; }

  /* The accent strokes: highlighter, not underline. Two layers per word — a
     wide blurred bloom (::before) and the wet-ink core over it (::after) — both
     running the same gradient, from the word's own colour at the start into
     mint at the tip, so the stroke reads as one pass of a pen that is running
     out rather than as a rule under the type. Both are set from two custom
     properties, so a line only has to declare its colour and its length. */
  .ma-hero-tagline span::before,
  .ma-hero-tagline span::after {
    content: '';
    position: absolute;
    left: var(--ma-stroke-l);
    right: var(--ma-stroke-r);
    border-radius: 999px;
    background: linear-gradient(90deg,
      var(--ma-stroke-c) 0%,
      var(--ma-stroke-c) 34%,
      rgba(134,232,205,0.92) 74%,
      rgba(134,232,205,0) 100%);
  }
  /* The bloom: three times the height, blurred out and dropped back, which is
     what gives the stroke its glow rather than a hard edge. */
  .ma-hero-tagline span::before {
    bottom: 0.01em;
    height: 0.13em;
    opacity: 0.5;
    filter: blur(0.055em);
  }
  /* The core. A whisper of blur on this one too — a pen edge is never crisp. */
  .ma-hero-tagline span::after {
    bottom: 0.055em;
    height: max(1.5px, 0.042em);
    filter: blur(0.012em);
    box-shadow: 0 0 0.5em rgba(134,232,205,0.45);
  }
  /* Colour and length, line by line. "Small" gets the shortest stroke, barely
     wider than the word; each one after it runs a little further, the way a
     hand loosens up down a note. */
  .ma-hero-tagline span:nth-child(1) {
    --ma-stroke-c: rgba(255,255,255,0.9);
    --ma-stroke-l: 16%; --ma-stroke-r: 8%;
  }
  .ma-hero-tagline span:nth-child(2) {
    --ma-stroke-c: rgba(255,255,255,0.9);
    --ma-stroke-l: 8%; --ma-stroke-r: 0%;
  }
  .ma-hero-tagline span:nth-child(3) {
    --ma-stroke-c: rgba(227,245,115,0.95);
    --ma-stroke-l: 4%; --ma-stroke-r: -3%;
  }
  .ma-hero-tagline span:nth-child(4) {
    --ma-stroke-c: rgba(227,245,115,0.95);
    --ma-stroke-l: 6%; --ma-stroke-r: -12%;
  }

  /* ═══ SECTION HEADERS ═══ */
  /* One fluid gutter for every band. Lands on 60px above 1442, ~43 at the
     laptop line, ~29 at the tablet line, ~19 on a 480 phone and the 16px floor
     at 320 — i.e. the old four hand-set values, but continuous between them. */
  .ma-band {
    position: relative;
    width: 100%;
    padding:
      var(--ma-band-y)
      calc(var(--ma-gutter) + var(--ma-safe-r))
      var(--ma-band-y)
      calc(var(--ma-gutter) + var(--ma-safe-l));
    z-index: 1;
  }
  .ma-band-inner { max-width: 1240px; margin: 0 auto; position: relative; }

  .ma-label {
    display: inline-flex; align-items: center; gap: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 12px; font-weight: 600; letter-spacing: 2.6px;
    text-transform: uppercase; color: #2bb3a3;
    margin-bottom: 18px;
  }
  .ma-label-num {
    font-variant-numeric: tabular-nums;
    color: rgba(43,179,163,0.55);
  }
  .ma-label-rule {
    width: 30px; height: 1px;
    background: linear-gradient(90deg, rgba(43,179,163,0.7), rgba(99,102,241,0.1));
  }

  .ma-h2 {
    font-family: 'Poppins', sans-serif;
    /* 26px at 320 -> ~29 at 480 -> ~33 at 734 -> ~39 at 1069 -> 46 at 1442,
       capped at 52. The same endpoints the old 30/38/52 step ladder hit, with
       the steps taken out. */
    font-size: clamp(26px, 1.78vw + 20.3px, 52px);
    font-weight: 600;
    line-height: 1.13;
    /* Eases with the size — a flat -1.2px is half a stem width at 26px */
    letter-spacing: clamp(-1.2px, -0.023em, -0.55px);
    color: var(--ma-ink);
    max-width: 15ch;
    /* 15ch + a hard wrap is exactly the shape that strands a single word on
       the last line; balance spreads the break points instead. */
    text-wrap: balance;
  }
  .ma-h2-grad {
    background: linear-gradient(120deg, #2bb3a3 0%, #4f9fd8 48%, #6366f1 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  /* Sections that drop the gradient entirely: the accent phrase carries no
     class of its own and the whole heading reads as one solid ink. */
  .ma-h2-ink { color: #0f2a3d; }
  .ma-lede {
    font-size: clamp(16px, 0.33vw + 14.9px, 19px);
    line-height: 1.78;
    color: var(--ma-body);
    max-width: 46ch;
    margin-top: 20px;
    text-wrap: pretty;
  }
  .ma-support {
    display: inline-block;
    margin-top: 26px;
    font-family: 'Poppins', sans-serif;
    font-size: clamp(15px, 1.05vw, 17px);
    font-weight: 500;
    color: #2f7d8f;
    letter-spacing: -0.2px;
    padding-left: 16px;
    border-left: 2px solid rgba(43,179,163,0.4);
    line-height: 1.6;
  }

  /* ═══ ALTERNATING FEATURE SECTIONS ═══ */
  .ma-feature { position: relative; }

  /* Colour-only wash for 01 Pulse, 02 Journal, 03 Companion, 06 Goals, the
     Privacy band and the closing CTA that continues it: an almost-white pastel
     base lifted by two very wide, very
     soft tints — one from the top-right, one from the lower-right. The stops
     are spread far apart and every layer fades to a fully transparent copy of
     its own colour, so the blend stays diffuse and never bands. */
  .ma-feature-tint,
  .ma-privacy,
  .ma-cta {
    background-color: #f5fbfc;
    background-image:
      radial-gradient(ellipse 150% 130% at 108% -14%,
        rgba(234,247,250,1) 0%,
        rgba(234,247,250,0.72) 26%,
        rgba(234,247,250,0.34) 52%,
        rgba(234,247,250,0.10) 74%,
        rgba(234,247,250,0) 100%),
      radial-gradient(ellipse 135% 120% at 104% 112%,
        rgba(232,246,247,1) 0%,
        rgba(232,246,247,0.66) 28%,
        rgba(232,246,247,0.30) 56%,
        rgba(232,246,247,0.08) 78%,
        rgba(232,246,247,0) 100%);
  }
  /* minmax(0, 1fr), not 1fr: a grid track's default min-content floor would let
     the phone render — which is deliberately wider than its own column — push
     the track open and drag the copy column off the page. Capping the floor at
     0 keeps the columns at a true 50/50 and lets the render overflow visually
     into the gutter, which is the intended composition. */
  .ma-feature-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: clamp(40px, 6vw, 96px);
    align-items: center;
  }
  .ma-feature-media { display: flex; justify-content: center; min-width: 0; }
  /* The plate is wider than its own column by design and its outer haze reaches
     under the copy, exactly as the reference comps do — so the copy is lifted
     into its own layer to guarantee the text always sits on top of it. */
  .ma-feature-copy { max-width: 560px; min-width: 0; position: relative; z-index: 1; }

  /* Phone on the right → copy first in the DOM order stays natural.
     Phone on the left → swap visual order without touching reading order. */
  .ma-feature-grid.ma-media-left .ma-feature-media { order: 1; }
  .ma-feature-grid.ma-media-left .ma-feature-copy  { order: 2; }
  .ma-feature-grid.ma-media-right .ma-feature-media { order: 2; }
  .ma-feature-grid.ma-media-right .ma-feature-copy  { order: 1; }
  .ma-feature-grid.ma-media-right .ma-feature-copy { justify-self: end; }

  /* ═══ SECTION 05 — WELLNESS CAROUSEL ═══ */
  .ma-wellness {
    background:
      radial-gradient(ellipse 90% 60% at 50% 0%, rgba(43,179,163,0.10) 0%, rgba(43,179,163,0) 60%),
      linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
    border-top: 1px solid rgba(43,179,163,0.10);
    border-bottom: 1px solid rgba(43,179,163,0.10);
  }
  .ma-wellness-head { text-align: center; max-width: 780px; margin: 0 auto 64px; }
  .ma-wellness-head .ma-h2 { max-width: none; margin: 0 auto; }
  .ma-wellness-head .ma-lede { margin: 20px auto 0; }
  .ma-wellness-head .ma-label { justify-content: center; }

  /* The orbit is absolutely positioned in percentages of this box, so the box
     has to keep a usable aspect on its own — a min-height alone lets the stage
     go wide-and-short on a laptop and the top/bottom chips land on the phone.
     min-height scales with the viewport and the ring is capped to whichever of
     width or height is scarcer, so the ring never outgrows its own stage. */
  .ma-wellness-stage {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: clamp(560px, 52vw, 700px);
  }
  .ma-wellness-stage .ma-wellness-shot { --ma-body-h: clamp(340px, 36vw, 500px); }
  .ma-wellness-ring {
    position: absolute;
    width: min(760px, 84vw, 88%); aspect-ratio: 1;
    border-radius: 50%;
    border: 1px solid rgba(43,179,163,0.14);
    pointer-events: none;
  }
  .ma-wellness-ring.ma-ring-inner {
    width: min(540px, 66vw, 62%);
    border-color: rgba(99,102,241,0.12);
  }

  .ma-chip {
    position: absolute;
    display: inline-flex; align-items: center; gap: 9px;
    padding: 12px 20px;
    border-radius: 100px;
    background: rgba(255,255,255,0.72);
    border: 1px solid rgba(43,179,163,0.22);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    box-shadow: 0 10px 30px rgba(20,70,105,0.10);
    font-family: 'Inter', sans-serif;
    font-size: 14px; font-weight: 600; letter-spacing: -0.1px;
    color: #2f5d70;
    white-space: nowrap;
    opacity: 0;
    transform: translateY(18px) scale(0.94);
    transition: opacity 0.8s ease var(--ma-delay, 0s), transform 0.8s cubic-bezier(0.22,1,0.36,1) var(--ma-delay, 0s);
    z-index: 3;
  }
  /* The orbit's left: percentages are the chip's *left edge*, so a long label
     on the right-hand arc (left: 74%) runs past the stage on a narrow laptop
     before the row layout takes over. Capping the width at the room actually
     left of the stage edge lets those two labels wrap to two lines instead of
     leaving the page — the only two that ever do. */
  .ma-chip-orbit {
    max-width: calc(100% - var(--ma-chip-x, 0%) - 8px);
    white-space: normal;
  }
  .ma-in .ma-chip { opacity: 1; transform: translateY(0) scale(1); }
  .ma-chip-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: linear-gradient(135deg, #2bb3a3, #6366f1);
    flex-shrink: 0;
  }
  .ma-chip-float {
    animation: maFloatSoft 6s ease-in-out infinite;
    animation-delay: var(--ma-float-delay, 0s);
  }
  .ma-chip-row { display: none; }

  /* ═══ HUMAN SUPPORT BAND ═══ */
  .ma-human {
    background: linear-gradient(150deg, #0d2437 0%, #113a52 55%, #0f2c42 100%);
    color: #ffffff;
    overflow: hidden;
  }
  .ma-human .ma-h2 { color: #ffffff; }
  /* Only the closing phrase carries the teal — the rest of the line stays white. */
  .ma-human .ma-h2-accent { color: #5ed6c6; }
  .ma-human .ma-lede { color: rgba(255,255,255,0.62); }
  .ma-human .ma-label { color: #5ed6c6; }
  .ma-human .ma-label-num { color: rgba(94,214,198,0.55); }
  .ma-human-grid {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: clamp(40px, 6vw, 88px);
    align-items: center;
  }
  /* This render is upright and sits in the canvas narrower than the tilted
     feature shots, so matching them by body height alone left it reading small
     beside the rest of the page. Sized up until its rendered body width lands
     with the feature devices' footprint. */
  .ma-human .ma-human-shot { --ma-body-h: clamp(460px, 54vw, 780px); }

  /* ═══ PRIVACY ═══
     The quietest band on the page. Everything here is timed off one class —
     the page observer puts .ma-in on the section, and each part reads its own
     delay from that single moment:
       100ms eyebrow · 220ms heading · 400ms first card, then 90ms apart
       ~820ms the icons play their one-shot micro-animations
     Nothing loops except the wave, and the wave is slow enough to argue about. */
  /* Background is the shared .ma-feature-tint wash up in the feature block —
     the same surface the Companion band sits on — so nothing is set here. */
  .ma-privacy {
    overflow: hidden;
    text-align: center;
    padding-bottom: 150px;
    opacity: 0;
    transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .ma-privacy.ma-in { opacity: 1; }

  .ma-priv-eyebrow {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .ma-priv-eyebrow-text {
    font-family: 'Inter', sans-serif;
    font-size: 12px; font-weight: 600;
    letter-spacing: 4.2px;
    text-transform: uppercase;
    color: #2bb3a3;
    opacity: 0;
    transition: opacity 0.55s ease 0.1s;
  }
  /* Grows out of nothing rather than fading — the only piece of the section
     that draws attention to its own arrival, and it takes 400ms to do it. */
  .ma-priv-eyebrow-rule {
    width: 46px; height: 1px;
    background: linear-gradient(90deg, rgba(43,179,163,0.15), rgba(43,179,163,0.75), rgba(43,179,163,0.15));
    transform: scaleX(0);
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.18s;
  }
  .ma-privacy.ma-in .ma-priv-eyebrow-text { opacity: 1; }
  .ma-privacy.ma-in .ma-priv-eyebrow-rule { transform: scaleX(1); }

  .ma-priv-h2 {
    max-width: 22ch;
    margin: 26px auto 0;
    opacity: 0;
    transform: translateY(14px);
    transition:
      opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.22s,
      transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.22s;
  }
  .ma-privacy.ma-in .ma-priv-h2 { opacity: 1; transform: translateY(0); }

  /* The breathing space between the claim and the evidence is the point */
  .ma-priv-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 22px;
    margin-top: clamp(56px, 6vw, 88px);
    text-align: left;
  }

  .ma-priv-card {
    display: flex;
    flex-direction: column;
    padding: 34px 30px 36px;
    border-radius: 26px;
    /* Near-white on a near-white wash — the separation comes from the edge and
       the shadow, not from a colour step. */
    background: rgba(255,255,255,0.92);
    border: 1px solid rgba(13,36,55,0.055);
    box-shadow: 0 1px 2px rgba(13,36,55,0.03), 0 10px 26px rgba(13,36,55,0.035);
    opacity: 0;
    transform: translateY(18px);
    transition:
      opacity 0.72s cubic-bezier(0.22, 1, 0.36, 1) var(--ma-delay, 0s),
      transform 0.72s cubic-bezier(0.22, 1, 0.36, 1) var(--ma-delay, 0s),
      box-shadow 0.35s ease,
      background 0.35s ease;
    will-change: opacity, transform;
  }
  .ma-privacy.ma-in .ma-priv-card { opacity: 1; transform: translateY(0); }

  /* Hover makes the surface more tangible — it never becomes a button. The
     text does not move, recolour or underline; only the plane it sits on.
     Scoped through .ma-in because the resting rule above already carries two
     classes; a bare :hover would lose the transform to it. */
  @media (hover: hover) {
    .ma-privacy.ma-in .ma-priv-card:hover {
      transform: translateY(-3px);
      background: #ffffff;
      box-shadow: 0 2px 4px rgba(13,36,55,0.04), 0 16px 34px rgba(13,36,55,0.07);
      /* Beat the entrance delay so the lift answers the pointer immediately */
      transition-delay: 0s;
    }
    .ma-privacy.ma-in .ma-priv-card:hover .ma-priv-icon { transform: translateY(-2px); opacity: 1; }
  }

  .ma-priv-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 52px; height: 52px;
    border-radius: 50%;
    background: rgba(43,179,163,0.09);
    color: #22867c;
    opacity: 0.88;
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease;
  }
  .ma-priv-icon svg { display: block; }

  .ma-priv-title {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(17px, 1.25vw, 19px);
    font-weight: 600;
    line-height: 1.28;
    letter-spacing: -0.4px;
    color: var(--ma-ink);
    margin-top: 26px;
    /* Two-column cards at 480-733 are narrow enough that "Encrypted end to
       end" drops its last word onto a line of its own. Balancing evens the
       two lines instead. */
    text-wrap: balance;
  }
  .ma-priv-copy {
    font-size: 15px;
    line-height: 1.66;
    color: var(--ma-body);
    margin-top: 10px;
    text-wrap: pretty;
  }
  /* The strongest claim on the page gets its weight from type, not decoration */
  .ma-priv-card-lead .ma-priv-title { font-size: clamp(18px, 1.35vw, 21px); }

  /* ── One-shot icon micro-animations ────────────────────────────────────── */
  @keyframes maPrivShackle {
    from { transform: translateY(-1.6px) scaleY(1.22); }
    to   { transform: none; }
  }
  @keyframes maPrivDraw { to { stroke-dashoffset: 0; } }
  @keyframes maPrivFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes maPrivLid {
    0%   { transform: none; }
    35%  { transform: rotate(-14deg); }
    100% { transform: none; }
  }
  @keyframes maPrivDrop {
    0%   { opacity: 0; transform: translateY(-3px); }
    30%  { opacity: 1; transform: translateY(-1px); }
    100% { opacity: 0; transform: translateY(6px); }
  }

  /* SVG transforms need their own box or the origin resolves against the
     whole viewport-sized user space instead of the shape. */
  .ma-priv-shackle, .ma-priv-lid, .ma-priv-drop {
    transform-box: fill-box;
    transform-origin: 50% 100%;
  }
  .ma-priv-lid { transform-origin: 8% 100%; }
  .ma-priv-drop { transform-origin: 50% 50%; }

  .ma-privacy.ma-in .ma-priv-shackle {
    animation: maPrivShackle 0.42s cubic-bezier(0.22, 1, 0.36, 1) var(--ma-icon-delay, 0.8s) both;
  }
  .ma-priv-check {
    stroke-dasharray: 11;
    stroke-dashoffset: 11;
  }
  .ma-privacy.ma-in .ma-priv-check {
    animation: maPrivDraw 0.4s cubic-bezier(0.65, 0, 0.35, 1) var(--ma-icon-delay, 0.8s) both;
  }
  /* Data exists first, then stops being passed around */
  .ma-priv-net { opacity: 0; }
  .ma-priv-slash {
    stroke-dasharray: 22;
    stroke-dashoffset: 22;
  }
  .ma-privacy.ma-in .ma-priv-net {
    animation: maPrivFadeIn 0.32s ease var(--ma-icon-delay, 0.8s) both;
  }
  .ma-privacy.ma-in .ma-priv-slash {
    animation: maPrivDraw 0.42s cubic-bezier(0.65, 0, 0.35, 1) calc(var(--ma-icon-delay, 0.8s) + 0.26s) both;
  }
  .ma-priv-drop { opacity: 0; }
  .ma-privacy.ma-in .ma-priv-lid {
    animation: maPrivLid 0.6s cubic-bezier(0.34, 1, 0.5, 1) var(--ma-icon-delay, 0.8s) both;
  }
  .ma-privacy.ma-in .ma-priv-drop {
    animation: maPrivDrop 0.5s ease-in-out calc(var(--ma-icon-delay, 0.8s) + 0.1s) both;
  }

  /* ── Ambient wave ──────────────────────────────────────────────────────── */
  .ma-priv-wave {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: clamp(58px, 7vw, 96px);
    pointer-events: none;
    overflow: hidden;
  }
  /* The path carries two full periods across 2400 units, so sliding it by
     exactly half its width loops without a seam. 80s for one pass — slow
     enough that it reads as the section breathing, not as an animation. */
  .ma-priv-wave svg {
    display: block;
    width: 200%;
    height: 100%;
    animation: maDriftWave 80s linear infinite;
  }
  /* Blue, not teal — it has to read as one more layer of the same pastel wash
     the band sits on, never as a green tide line under it. */
  .ma-priv-wave path { fill: rgba(94,196,245,0.10); }
  @keyframes maDriftWave {
    from { transform: translate3d(0, 0, 0); }
    to   { transform: translate3d(-50%, 0, 0); }
  }

  /* ═══ CLOSING CTA ═══
     No ground of its own: it carries the same wash as the Privacy band above it
     (set with .ma-feature-tint), so the two read as one continuous surface and
     only spacing separates them. */
  .ma-cta {
    position: relative;
    text-align: center;
    overflow: hidden;
  }
  .ma-cta .ma-h2 { max-width: none; margin: 0 auto; }
  .ma-cta .ma-lede { margin: 22px auto 0; }
  .ma-cta-actions {
    display: flex; flex-wrap: wrap; gap: 14px;
    justify-content: center; margin-top: 40px;
  }
  .ma-cta-stores {
    display: flex; flex-wrap: wrap; gap: 12px;
    justify-content: center; margin-top: 22px;
  }

  /* ═══ SCROLL CHAPTERS ═══
     Four chapters handed over to each other by scroll. The whole section is one
     tall track with a sticky, viewport-tall stage inside it; every chapter is an
     absolutely-positioned layer filling that stage, so nothing ever moves,
     resizes or reflows — the only thing that changes is how much of each card is
     clipped away. All of the timing lives in ScrollChapters.jsx; the only thing
     CSS owns here is the geometry that clip is cut out of.

     Cards run the full height of the stage and pass *under* the floating nav
     rather than being inset below it, which is what lets a card's own top edge
     be the thing the clip reveals. Since the clip windows overlap, two or three
     cards are partially on the stage at nearly every scroll position.

     The page colour is a deliberate step away from the rest of the page: it is
     what shows through the gap between two half-clipped cards, so it has to read
     as *background*, not as a third card. It fades in and out at the section's
     edges so the band doesn't butt up against the surrounding blue. */
  .ma-chapters {
    position: relative;
    z-index: 1;
    padding-top: var(--ma-band-y);
    background: linear-gradient(180deg,
      #e9f5f7 0%,
      #cfe8eb 6%,
      #c2e2e6 18%,
      #c2e2e6 84%,
      #cfe8eb 95%,
      #e8f4f7 100%);
  }

  .ma-chapters-intro {
    max-width: 1240px;
    margin: 0 auto;
    padding:
      0
      calc(var(--ma-gutter) + var(--ma-safe-r))
      clamp(44px, 5vw, 62px)
      calc(var(--ma-gutter) + var(--ma-safe-l));
    text-align: center;
  }
  .ma-chapters-intro .ma-label { justify-content: center; }
  .ma-chapters-intro .ma-h2 { max-width: 20ch; margin: 0 auto; }
  .ma-chapters-sub {
    margin: 18px auto 0;
    max-width: 40ch;
    font-size: clamp(16px, 1.2vw, 19px);
    line-height: 1.7;
    color: var(--ma-body);
  }
  .ma-chapters-quote {
    display: inline-block;
    margin-top: 26px;
    font-family: 'Poppins', sans-serif;
    font-size: clamp(15px, 1.05vw, 17px);
    font-weight: 500;
    color: #2f7d8f;
    letter-spacing: -0.2px;
    padding-left: 16px;
    border-left: 2px solid rgba(43,179,163,0.4);
    line-height: 1.6;
    text-align: left;
  }

  /* The track exists only to buy scroll distance. The handover windows overlap,
     so there are no rests to budget for — the 300vh of travel below the first
     viewport is spent entirely on handovers, one every ~60vh and each lasting
     ~120vh. A handover is something you scroll *through*, not past. */
  .ma-chapters-track { position: relative; height: 400vh; }
  .ma-chapters-stage {
    position: sticky;
    top: 0;
    height: 100vh;
    overflow: hidden;
  }

  /* No vertical padding: the card is the stage. Insetting it below the nav would
     mean the clip's top edge and the card's top edge were different lines, and
     the reveal reads as a blind rolling down the card only when they are the
     same line. The nav floats over the card instead. */
  .ma-chapter {
    position: absolute;
    inset: 0;
    display: flex;
    /* The margin that makes the card read as ~75% of the section, not ~95% —
       12.5vw a side, measured off the reference. */
    padding: 0 clamp(20px, 12.5vw, 300px);
  }

  /* clip-path cuts a plain rectangle out of an element that is *itself* rounded,
     so a card keeps its radius on the edges the clip hasn't reached and comes
     out square on the edge it has — which is exactly how a card reads when it is
     halfway off the stage. */
  .ma-chapter-card {
    flex: 1;
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    align-items: center;
    gap: 40px;
    padding: 0 clamp(24px, 3.4vw, 58px);
    border-radius: 30px;
    background:
      radial-gradient(115% 90% at 90% 14%, rgba(196,229,235,0.60) 0%, rgba(196,229,235,0) 62%),
      radial-gradient(100% 85% at 2% 98%, rgba(206,234,238,0.55) 0%, rgba(206,234,238,0) 64%),
      #fbfdfd;
    box-shadow: 0 34px 74px -44px rgba(6,58,70,0.5);
    will-change: clip-path;
  }

  .ma-chapter-copy {
    max-width: 32ch;
    will-change: opacity, transform;
  }
  .ma-chapter-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 34px;
    padding: 0 11px;
    margin-bottom: 26px;
    border-radius: 11px;
    background: rgba(43,179,163,0.15);
    color: #1c8b80;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .ma-chapter-h {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(22px, 1.3vw + 8px, 36px);
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: clamp(-1.1px, -0.03em, -0.5px);
    /* Sampled off the mockups — a dark navy ink, not pure black. */
    color: #001523;
    text-wrap: balance;
  }
  .ma-chapter-p {
    margin-top: 22px;
    max-width: 42ch;
    font-size: clamp(16px, 1.15vw, 18px);
    line-height: 1.72;
    color: var(--ma-body);
  }

  /* Sized to the *device* inside the PNG rather than to the PNG's canvas: the
     image is scaled up by 1/hf and pulled up by its own top margin, so this box
     and the device body are the same rectangle. That is what makes a clip of
     "30% off the bottom" take 30% off the phone rather than 30% off a canvas
     that is mostly empty. --ma-ch-h is a height, never a width, so every
     chapter's device lands at exactly the same height and one clip schedule
     reads identically through all three handovers.

     The height is a min() of two limits because the two things it has to satisfy
     scale on different axes, and on most screens they cannot both be satisfied.
     86vh is what the reference uses — a device that tall fills the card, which
     is what makes the clip land on the *phone*: the handover has to reveal a
     status bar, not a band of blank card. 47vw stops it outgrowing a card whose
     own width is only 75vw. The reference's own window is 2.13:1, wide enough
     that 86vh also happens to land at ~25% of the card; on a 16:10 laptop the
     same height reads nearer 31%. Height wins that trade — the mechanic depends
     on it, and the width figure is a consequence of the window, not a target. */
  .ma-chapter-device {
    --ma-ch-h: clamp(320px, min(86vh, 47vw), 1250px);
    position: relative;
    justify-self: center;
    height: var(--ma-ch-h);
    width: calc(var(--ma-ch-h) * 0.666667 * var(--ma-ch-wf) / var(--ma-ch-hf));
  }
  .ma-chapter-device img {
    position: absolute;
    left: 50%;
    top: calc(-1 * var(--ma-ch-tf) * var(--ma-ch-h) / var(--ma-ch-hf));
    height: calc(var(--ma-ch-h) / var(--ma-ch-hf));
    width: auto;
    /* The canvas is wider than the body; the spill is transparent and is caught
       by the stage's overflow. .ma-page's global cap would squash it instead. */
    max-width: none;
    transform: translateX(calc(var(--ma-ch-cx) * -100%));
  }

  /* Stacked and reduced-motion layouts drop the mechanic entirely — there is no
     room for a two-column card and no sticky stage to scrub against, so the four
     chapters become four plain cards. The !important guards are load-bearing:
     they beat the inline clip/opacity the effect may already have written before
     the viewport crossed the breakpoint. This query is the exact pair the
     component matchMedias on, so JS stops writing at the same moment. */
  /* Tablet and below (<= 1068, the tablet line) drop the mechanic entirely —
     there is no room for a two-column card and no sticky stage to scrub
     against, so the four chapters become four plain cards. The !important
     guards are load-bearing: they beat the inline clip/opacity the effect may
     already have written before the viewport crossed the breakpoint. This
     query is the exact pair ScrollChapters.jsx matchMedias on, so JS stops
     writing at the same moment — change one and you must change the other. */
  @media (max-width: 1068px), (prefers-reduced-motion: reduce) {
    .ma-chapters-track { height: auto; }
    .ma-chapters-stage { position: static; height: auto; overflow: visible; }
    .ma-chapter {
      position: relative; inset: auto; display: block;
      padding: 0 calc(var(--ma-gutter) + var(--ma-safe-l)) 22px calc(var(--ma-gutter) + var(--ma-safe-r));
    }
    .ma-chapter-card {
      display: block;
      padding: clamp(34px, 5vw, 46px) clamp(20px, 5vw, 46px) 0;
      text-align: center;
      clip-path: none !important;
      visibility: visible !important;
    }
    .ma-chapter-copy {
      max-width: none;
      opacity: 1 !important;
      transform: none !important;
    }
    .ma-chapter-p { margin-left: auto; margin-right: auto; }
    /* Width-driven, not 148vw: the device's *width* is what has to fit the
       card, and width = height * 0.489 for these renders. 118vw keeps a 320px
       phone's device at ~185px inside a ~228px card interior, and the 560 cap
       stops it filling a whole iPad screen. */
    .ma-chapter-device {
      --ma-ch-h: clamp(340px, 118vw, 560px);
      margin: 34px auto 0;
    }
  }

  /* ═══ RESPONSIVE ═══
     Five ranges, matching the ladder declared in :root. Padding, gutters and
     type are already fluid via clamp(), so what follows is only the structural
     work: columns stacking, the orbit becoming a row, the phone renders being
     re-sized against a narrower container.

     Nothing here touches the SHOT constants or the podium maths. Every phone
     is still sized by one number — --ma-body-h — and the plate is still
     derived from it, exactly as at desktop. All a breakpoint does is hand that
     one number a different clamp. */

  /* ── >= 1442px · LARGE DESKTOP ─────────────────────────────────────────── */
  @media (min-width: 1442px) {
    /* The composition stops growing here — the shells are already at their
       max-width, so letting the renders keep scaling with vw would just make
       the phones bigger than the copy beside them. */
    .ma-hero-inner { gap: 64px; }
    .ma-shot-stage { --ma-body-h: 560px; }
    .ma-wellness-stage .ma-wellness-shot { --ma-body-h: 500px; }
    .ma-human .ma-human-shot { --ma-body-h: 780px; }
  }

  /* ── 1069-1441px · LAPTOP / SMALL DESKTOP ──────────────────────────────── */
  @media (min-width: 1069px) and (max-width: 1441px) {
    /* Between the laptop and large-desktop lines the phone pair reaches the
       container edge and the only sky left is the page gutter. The note keeps
       its place beside the phones by anchoring to the window instead, at the
       size that strip will hold; below 1180 the strip is narrower than the
       shortest word, so it stands down. */
    .ma-hero-tagline {
      /* Still hung off the container edge, but pulled back into the gutter by
         about the width of that gutter, which is all the sky there is. */
      margin-left: -1.6vw;
      font-size: clamp(18px, 1.45vw, 21px);
      top: 27%;
    }
    @media (max-width: 1179px) { .ma-hero-tagline { display: none; } }
    /* The orbit's outer chips sit at left: 72-74%, so on the narrowest
       two-column widths the labels are the first thing to run out of room.
       One step down in type buys back ~24px per chip, which is enough to
       carry the orbit to the tablet line without any of them wrapping. */
    .ma-chip-orbit { font-size: 13px; padding: 11px 17px; }
  }

  /* ── <= 1068px · TABLET AND BELOW ──────────────────────────────────────── */
  /* The tablet line is where every two-column layout on the page collapses:
     hero, features, human support and the chapter mechanic all stack here, and
     the wellness orbit becomes a row. It is one line for all of them on
     purpose — a page that stacks its sections at four different widths reads
     as four separate pages. */
  @media (max-width: 1068px) {
    .ma-hero { min-height: auto; }
    .ma-hero h1 {
      /* The stacked curve: 34px at 360 rising to 49px at 1068. The base rule
         resolves to 49.2px at 1069, so the two curves meet across the line —
         crossing the tablet breakpoint changes the layout, not the type. */
      font-size: clamp(34px, 2.12vw + 26.4px, 50px);
    }
    .ma-hero-inner { grid-template-columns: minmax(0, 1fr); gap: clamp(40px, 6vw, 56px); text-align: center; }
    .ma-hero-lede { margin-left: auto; margin-right: auto; }
    .ma-hero-cta, .ma-hero-stores, .ma-hero-note { justify-content: center; }
    /* Stacked layout — cap the cluster so the phone pair keeps its desktop
       proportions instead of ballooning to the full page width. */
    .ma-hero-cluster { max-width: 620px; margin: 0 auto; }
    /* Stacked: the phones are centred and capped, so the note moves off the
       container edge and into the sky beside the lower half of the pair. */
    .ma-hero-tagline {
      left: auto; right: 0; margin-left: 0;
      top: auto; bottom: 30%;
      font-size: clamp(24px, 3.4vw, 34px);
    }
    /* Below this the sky either side of the pair is narrower than the note,
       and the hero belongs to the headline and the CTA. Decorative, so it
       switches off rather than taking a line on the structural ladder. */
    @media (max-width: 859px) {
      .ma-hero-tagline { display: none; }
    }

    .ma-feature-grid { grid-template-columns: minmax(0, 1fr); gap: clamp(40px, 6vw, 56px); }
    /* Visual first, copy second — whichever side the phone sat on when the
       grid was two columns. Apple leads with the image; more to the point, a
       consistent rule is what stops the stacked page feeling shuffled. Only
       the visual order changes: the DOM, and so the reading order, is
       untouched. */
    .ma-feature-grid.ma-media-left .ma-feature-media,
    .ma-feature-grid.ma-media-right .ma-feature-media { order: 1; }
    .ma-feature-grid.ma-media-left .ma-feature-copy,
    .ma-feature-grid.ma-media-right .ma-feature-copy { order: 2; justify-self: center; text-align: center; }
    /* Side by side, the plate's base overhangs into the band's own padding and
       nothing sits under it. Stacked, the copy moves directly beneath — so the
       stage reserves exactly the height the plinth spills past the device:
         plate bottom - device bottom. */
    .ma-staged {
      padding-bottom: calc(
        var(--ma-body-h) * (
          var(--ma-canvas-k) * (var(--ma-shot-bottom) - 1) - var(--ma-shot-sink, 0)
        )
        + var(--ma-plate-w) * var(--ma-pod-ar) * (var(--ma-pod-foot) - var(--ma-pod-disc))
      );
    }
    /* Stacked, nothing sits beside the plate any more — the copy is below it —
       so the scenery gets its full spread back on both sides. */
    .ma-staged-left > .ma-podium,
    .ma-staged-right > .ma-podium {
      -webkit-mask-image: none;
              mask-image: none;
    }
    /* Re-sized for a full-width container rather than a half-width column.
       Held *below* the desktop clamp on purpose: with the masks off, the whole
       plate is visible, and at the desktop size its scenery would run a long
       way past the band on a 734px tablet. This clamp keeps the widest plate
       (Pulse, grip 1.25) within ~40px a side of the band at 1068 and leaves
       the plinth fully visible all the way down to 734. */
    .ma-shot-stage { --ma-body-h: clamp(380px, 46vw, 500px); }
    .ma-wellness-stage .ma-wellness-shot { --ma-body-h: clamp(360px, 44vw, 460px); }
    /* Upright and narrow in its canvas, so it needs a bigger body height than
       the tilted renders to read at the same size. Reined in from the desktop
       clamp, which at 1024 stacked was putting a 780px-tall device on screen. */
    .ma-human .ma-human-shot { --ma-body-h: clamp(420px, 58vw, 600px); }

    .ma-feature-copy { max-width: 620px; margin: 0 auto; }
    .ma-lede { margin-left: auto; margin-right: auto; }
    .ma-label { justify-content: center; }
    .ma-h2 { max-width: none; }
    .ma-support { border-left: none; padding-left: 0; padding-top: 14px; border-top: 2px solid rgba(43,179,163,0.3); }

    .ma-human-grid { grid-template-columns: minmax(0, 1fr); gap: clamp(40px, 5vw, 48px); text-align: center; }

    /* Two by two rather than four across — the row would shrink each card past
       the padding that makes it read as a surface. The left-to-right rhythm
       still holds, it just wraps once. */
    .ma-priv-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
    .ma-privacy { padding-bottom: clamp(110px, 13vw, 130px); }

    /* Chips leave orbit and become a tidy wrapped row. This is the width at
       which the ring stops having room to breathe: the outer ring is
       min(760px, 84vw, 88%), and below ~1069 the arc it draws is close enough
       to the phone that the chips read as sitting on the device rather than
       around it. */
    .ma-wellness-stage { min-height: 0; flex-direction: column; }
    .ma-wellness-ring { display: none; }
    .ma-chip { position: static; transform: translateY(18px) scale(0.96); }
    .ma-chip-orbit { display: none; }
    .ma-chip-row {
      display: flex; flex-wrap: wrap; gap: 10px;
      justify-content: center; margin-top: clamp(32px, 5vw, 44px); max-width: 640px;
    }
  }

  /* ── <= 733px · PHONE (both sizes) ─────────────────────────────────────── */
  @media (max-width: 733px) {
    .ma-hero-cta { flex-direction: column; align-items: stretch; width: 100%; }
    .ma-btn-primary, .ma-btn-glass, .ma-btn-ghost { width: 100%; font-size: 16px; padding: 15px 24px; }
    .ma-hero-stores { justify-content: center; }
    .ma-store { flex: 1 1 46%; justify-content: center; padding: 10px 14px; }
    /* Too little width for a side-by-side pair — go back to overlapping,
       with the front phone larger, as the old mobile hero did. The pair is
       allowed to run past the right edge of the screen: .ma-hero has its own
       overflow: hidden, so the back phone bleeds off-canvas the way hero
       imagery is meant to, rather than both phones being shrunk to fit. */
    .ma-hero-cluster { aspect-ratio: 1 / 1.24; }
    .ma-hero-phone-front { --ma-body-w: 58%; left: 33%; top: 52%; }
    .ma-hero-phone-back  { --ma-body-w: 44%; left: 100%; top: 6%; }

    /* Body heights, not canvas widths — the transparent margin around each
       render varies, and any of it that spills past the viewport is clipped
       harmlessly by the page's overflow-x. Verified at 320px: every render's
       opaque body lands inside the gutters, and every plinth is fully on
       screen — only scenery is ever clipped. */
    .ma-shot-stage { --ma-body-h: clamp(300px, 92vw, 420px); }
    /* A phone-width column can't hold the full plate, so the environment is
       drawn in tighter around the device. Everything is anchored off the top
       face, so easing --ma-pod-grip keeps the phone grounded — it just brings
       the mountains closer in, and any soft outer edge left over is clipped by
       the page's overflow-x rather than being allowed to scroll. */
    .ma-staged { --ma-pod-grip: 1.24; }
    .ma-staged-upright { --ma-pod-grip: 1.5; }
    .ma-wellness-stage .ma-wellness-shot { --ma-body-h: clamp(300px, 88vw, 420px); }
    .ma-human .ma-human-shot { --ma-body-h: clamp(360px, 96vw, 520px); }
    .ma-cta-actions { flex-direction: column; align-items: stretch; }

    .ma-priv-h2 { max-width: none; }
    .ma-priv-card { padding: 28px 24px 30px; border-radius: 22px; }
    .ma-priv-title { margin-top: 20px; }
  }

  /* ── <= 479px · SMALL PHONE ────────────────────────────────────────────── */
  @media (max-width: 479px) {
    .ma-store { flex: 1 1 100%; }
    /* The badge is one unbreakable line, so on the narrowest phones the
       sparkles give up room before the pill does. */
    .ma-flag-spark { width: 24px; height: 24px; }
    .ma-hero-flagrow { gap: 6px; }
    .ma-hero-flag { padding: 11px 16px; letter-spacing: 0.5px; }
    .ma-chip { font-size: 13px; padding: 10px 16px; }
    .ma-priv-grid { grid-template-columns: minmax(0, 1fr); margin-top: 44px; }
    .ma-chapters-quote, .ma-support { font-size: 15px; }
  }

  /* ── LANDSCAPE PHONE ───────────────────────────────────────────────────── */
  /* 667x375 and 812x375 are wide enough to read as "tablet" by width alone,
     which would give them a full-height hero and a 560px chapter device in a
     375px-tall viewport. Height is the scarce axis here, so everything that is
     normally sized off width gets a vh ceiling instead. */
  @media (orientation: landscape) and (max-height: 560px) {
    .ma-hero {
      min-height: auto;
      padding-top: calc(var(--nav-height) + 16px);
      padding-bottom: 44px;
    }
    .ma-hero h1 { font-size: clamp(30px, 5.6vh, 44px); margin-top: 16px; }
    .ma-hero-cluster { max-width: min(420px, 62vh); }
    .ma-band { --ma-band-y: clamp(48px, 12vh, 74px); }
    .ma-shot-stage { --ma-body-h: clamp(280px, 78vh, 420px); }
    .ma-wellness-stage .ma-wellness-shot { --ma-body-h: clamp(260px, 72vh, 380px); }
    .ma-human .ma-human-shot { --ma-body-h: clamp(320px, 96vh, 480px); }
    .ma-chapter-device { --ma-ch-h: clamp(260px, 76vh, 420px); }
  }

  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    .ma-reveal, .ma-chip, .ma-shot-stage {
      transition-duration: 0.01ms !important;
      opacity: 1 !important;
      transform: none !important;
    }
    .ma-in .ma-float, .ma-float.ma-in, .ma-chip-float,
    .ma-hero-phone img { animation: none !important; }
    .ma-hero-bg { transform: none !important; }
    /* Privacy arrives already arrived — everything at its resting state, the
       icons drawn rather than drawing, the wave held still. */
    .ma-privacy, .ma-priv-eyebrow-text, .ma-priv-h2, .ma-priv-card {
      transition-duration: 0.01ms !important;
      opacity: 1 !important;
      transform: none !important;
    }
    .ma-priv-eyebrow-rule { transition-duration: 0.01ms !important; transform: scaleX(1) !important; }
    .ma-privacy.ma-in .ma-priv-shackle,
    .ma-privacy.ma-in .ma-priv-check,
    .ma-privacy.ma-in .ma-priv-net,
    .ma-privacy.ma-in .ma-priv-slash,
    .ma-privacy.ma-in .ma-priv-lid,
    .ma-priv-wave svg { animation: none !important; }
    .ma-priv-check, .ma-priv-slash { stroke-dashoffset: 0 !important; }
    .ma-priv-net { opacity: 1 !important; }
    /* The falling entry has no resting state worth showing, so it just isn't */
    .ma-priv-drop { display: none !important; }
    .ma-eyebrow, .ma-hero h1, .ma-hero-lede, .ma-hero-cta, .ma-hero-stores, .ma-hero-note,
    .ma-hero-flag, .ma-hero-flagwrap, .ma-hero-flagrow, .ma-hero-tagline,
    .ma-flag-spark {
      animation: none !important;
    }
    /* The badge keeps its look, minus every loop: sparkles rest at full size
       and opacity, and the light sweep never enters the pill. */
    .ma-flag-spark { transform: none !important; opacity: 1 !important; }
    .ma-hero-flag::after { display: none !important; }
  }
`;
